/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useMemo, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Text from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useUserQuery, useUserActions } from '../../actions/UserActions';

import { confirmDialog } from '../../utils/dialog';
import Link from '@mui/material/Link';
import { track } from '../../utils/track';
import { IS_SINGLE_TENENT_MODE } from '../../constants';

export default function ModelSelector({ className }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { user } = useUserQuery();
  const { updateUser } = useUserActions();
  const [userModel, setUserModel] = useState(user?.default_model || 'klang');

  useEffect(() => {
    if (user?.default_model) {
      setUserModel(user.default_model);
    }
  }, [user]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async ({ value: model }) => {
    const switchedBefore = localStorage.getItem('switched_model');

    if (!switchedBefore && user?.default_model === 'klang') {
      const ok = await confirmDialog({
        title: t('Viktig information om externa AI-modeller i Klang.ai'),
        content: (
          <div>
            <Trans t={t}>
              När du byter modell i Klang.ai kommer data delas med leverantören
              av modellen. Vi har avtal som reglerar att din data hanteras på
              ett säkert och korrekt sätt med alla våra underleverantörer. Mer
              information kan du hitta i våra{' '}
              <Link href="https://app.klang.ai/info/terms.html" target="_blank">
                användarvillkor
              </Link>
              . Välj "Klang.ai" om du inte vill att någon data ska lämna
              Klang.ai:s servrar.
            </Trans>
            <Text fontWeight="700">
              {t('Vill du aktivera tredjepartsmodeller?')}
            </Text>
          </div>
        ),
      });
      if (!ok) {
        return;
      }
      localStorage.setItem('switched_model', true);
    }
    setUserModel(model);
    track('model_switch', { model });
    updateUser({ defaultModel: model });
    setAnchorEl(null);
  };

  const models = useMemo(
    () => ({
      klang: {
        name: 'Klang.ai',
        value: 'klang',
        description: t('Vår egen modell för högsta möjliga säkerhet'),
        country: t('Klang.ai (Europa)'),
        cost: 1,
      },
      'gpt-4o': {
        name: 'GPT-4o',
        value: 'gpt-4o',
        description: t('OpenAIs senaste modell'),
        country: t('USA'),
        cost: 2,
      },
      gemini: {
        name: 'Gemini Flash',
        value: 'gemini',
        description: t('Googles mest kraftfulla och snabbaste modell'),
        country: t('USA'),
        cost: 2,
      },
    }),
    [t]
  );

  if (!user?.workspace?.allow_gpt || IS_SINGLE_TENENT_MODE) return null;

  return (
    <div
      css={css`
        display: flex;
        gap: 8px;
        align-items: center;
      `}
      className={className}
    >
      <Text
        variant="body2"
        color="textSecondary"
        css={css`
          line-height: 34.5px;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.17px;
        `}
      >
        {t('Modell')}:
      </Text>
      <Button onClick={handleClick} endIcon={<ArrowDropDownIcon />}>
        {models[userModel]?.name}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {Object.values(models).map((model) => (
          <MenuItem
            key={model.value}
            onClick={() => handleSelect(model)}
            disabled={
              model.value !== 'klang' && user?.workspace?.type === 'trial'
            }
            css={css`
              border: 1px solid
                ${model.value === userModel
                  ? 'var(--primary-50)'
                  : 'transparent'};
              background-color: ${model.value === userModel
                ? 'var(--primary-50)'
                : 'transparent'};

              &:hover {
                background-color: ${model.value === userModel
                  ? 'var(--primary-50)'
                  : 'transparent'};
              }
            `}
          >
            <ListItemText
              primary={
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  `}
                >
                  <Text
                    css={css`
                      text-overflow: ellipsis;
                      overflow: hidden;
                      white-space: nowrap;
                    `}
                    variant="subtitle1"
                  >
                    {model.name}
                  </Text>
                  {model.value !== 'klang' &&
                    user?.workspace?.type === 'trial' && (
                      <Chip label={t('Start')} size="small" color="primary" />
                    )}
                </div>
              }
              secondary={
                <div
                  css={css`
                    padding-left: 1rem;
                    display: flex;
                    flex-direction: column;
                  `}
                >
                  <Text>{model.description}</Text>
                  <Text variant="caption">
                    {t('Serverplats: {{country}}', { country: model.country })}
                  </Text>
                  <Text variant="caption">
                    {t('Använder: {{cost}} AI-svar', { cost: model.cost })}
                  </Text>
                </div>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
